import React, { useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { useDropzone } from 'react-dropzone'

import Icon from '../icon'
import useApi from '../../hooks/useApi'
import upload from '../../constants/upload'
import Styles from './MediaUpload.module.scss'

export default function MediaUpload({
  imageUrl,
  onChange,
  label,
  dimensions,
  accept = 'image/png, image/x-png,image/gif,image/jpeg, image/jpg, image/heic',
  onError,
}) {
  const { t } = useTranslation()
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState(null)
  const uploadImage = 'images/uploading-image.gif'

  const formatFileSize = bytes => {
    const mb = bytes / (1024 * 1024)
    return `${Math.round(mb)}MB`
  }

  const {
    getMediaAttachmentUploadSignedUrl,
    uploadFileToS3,
    createMediaAttachment,
  } = useApi()

  const handleFileUpload = useCallback(
    async file => {
      if (!file.type.startsWith('image/')) {
        const errorMsg = t('business.edit.upload_image_only')
        setError(errorMsg)
        if (onError) {
          onError(errorMsg)
        } else {
          // eslint-disable-next-line no-alert
          alert(errorMsg)
        }
        return
      }

      if (file.size >= upload.sizeLimit) {
        const maxSizeMB = formatFileSize(upload.sizeLimit)
        const errorMsg = `File size must be less than ${maxSizeMB}`
        setError(errorMsg)
        if (onError) {
          onError(errorMsg)
        } else {
          // eslint-disable-next-line no-alert
          alert(errorMsg)
        }
        return
      }

      setError(null)
      setUploading(true)
      const fileUpload = new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = loaded => {
          resolve({
            fileObject: file,
            direct_upload_url: loaded.target.result,
            uploading: true,
            uploadId:
              performance.now().toString(36) +
              Math.random().toString(36).substr(2),
            upload_content_type: file.type,
            upload_file_name: file.name,
            upload_file_size: file.size,
            media_attachment_type: 'business',
          })
        }
        reader.onerror = reject
        reader.readAsDataURL(file)
      })

      try {
        const fileResponse = await fileUpload
        const signedUrlResponse = await getMediaAttachmentUploadSignedUrl({
          filename: fileResponse.upload_file_name,
        })
        const { data } = signedUrlResponse
        const uploadResponse = await uploadFileToS3(
          data.upload.url,
          fileResponse.fileObject,
          data.upload.content_type,
        )

        if (uploadResponse.status === 200) {
          const uploadData = { ...fileResponse }
          delete uploadData.uploading
          delete uploadData.uploadId
          uploadData.direct_upload_url = data?.upload?.url?.split('?')[0] ?? ''

          const createResponse = await createMediaAttachment({
            media_attachment: uploadData,
          })

          if (createResponse.status === 201) {
            onChange(createResponse.data)
            setError(null)
          }
        }
      } catch (error) {
        console.error('Upload error:', error)
        const errorMsg = t('business.edit.upload_failed')
        setError(errorMsg)
        if (onError) {
          onError(errorMsg)
        } else {
          // eslint-disable-next-line no-alert
          alert(errorMsg)
        }
      } finally {
        setUploading(false)
      }
    },
    [
      onChange,
      onError,
      t,
      getMediaAttachmentUploadSignedUrl,
      uploadFileToS3,
      createMediaAttachment,
    ],
  )

  const onDrop = useCallback(
    acceptedFiles => {
      if (acceptedFiles.length > 0) {
        handleFileUpload(acceptedFiles[0])
      }
    },
    [handleFileUpload],
  )

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    accept: accept.split(',').reduce((acc, type) => {
      acc[type.trim()] = []
      return acc
    }, {}),
    multiple: false,
    disabled: uploading,
    noClick: true,
  })

  const handleRemove = useCallback(
    e => {
      e.stopPropagation()
      onChange(null)
    },
    [onChange],
  )

  return (
    <div className={Styles.container}>
      {label && (
        <label className={Styles.label}>
          {label}
          {dimensions && (
            <span className={Styles.dimensions}>({dimensions})</span>
          )}
        </label>
      )}
      <div
        {...getRootProps()}
        className={`${Styles.uploadArea} ${
          isDragActive ? Styles.dragActive : ''
        } ${uploading ? Styles.uploading : ''}`}
      >
        <input {...getInputProps()} />
        {uploading ? (
          <div className={Styles.uploadingContent}>
            <img src={uploadImage} alt="Uploading" />
            <div>Uploading...</div>
          </div>
        ) : imageUrl ? (
          <div className={Styles.previewContainer}>
            <img
              src={
                imageUrl?.thumbnail_url ??
                imageUrl?.direct_upload_url ??
                imageUrl
              }
              alt="Preview"
              className={Styles.previewImage}
            />
            <button
              type="button"
              className={Styles.removeButton}
              onClick={handleRemove}
            >
              <Icon name="close" />
            </button>
          </div>
        ) : (
          <div className={Styles.placeholder}>
            <Icon name="upload" className={Styles.placeholderIcon} />
            <div>drag and drop image</div>
          </div>
        )}
      </div>
      {error && (
        <div className="alert alert-danger mt-2" role="alert">
          {error}
        </div>
      )}
      <button
        type="button"
        className="btn btn-primary mt-2"
        onClick={open}
        disabled={uploading}
      >
        Upload Media
      </button>
    </div>
  )
}

MediaUpload.propTypes = {
  imageUrl: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      thumbnail_url: PropTypes.string,
      direct_upload_url: PropTypes.string,
    }),
  ]),
  onChange: PropTypes.func.isRequired,
  onError: PropTypes.func,
  label: PropTypes.string,
  dimensions: PropTypes.string,
  accept: PropTypes.string,
}
